import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import Landing from "@/Landing";
import Admin from "@/Admin";
import EmailSuite from "@/EmailSuite";
import AmbassadorJoin from "@/AmbassadorJoin";
import AmbassadorLogin from "@/AmbassadorLogin";
import AmbassadorPortal from "@/AmbassadorPortal";
import Ambassadors from "@/Ambassadors";
import AuthCallback from "@/AuthCallback";
import PaymentResult from "@/PaymentResult";
import ManageSubscription from "@/ManageSubscription";
import Review from "@/Review";
import Buy from "@/Buy";
import Contact from "@/Contact";
import Legal from "@/Legal";
import EmailPopup from "@/components/EmailPopup";
import PromoBanner from "@/components/PromoBanner";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/buy" element={<Buy />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/email-suite" element={<EmailSuite />} />
      <Route path="/ambassador" element={<AmbassadorPortal />} />
      <Route path="/ambassador/login" element={<AmbassadorLogin />} />
      <Route path="/ambassador/join/:token" element={<AmbassadorJoin />} />
      <Route path="/ambassadors" element={<Ambassadors />} />
      <Route path="/payment/success" element={<PaymentResult kind="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult kind="cancel" />} />
      <Route path="/manage" element={<ManageSubscription />} />
      <Route path="/review/:token" element={<Review />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/refund-policy" element={<Legal slugOverride="refund-policy" />} />
      <Route path="/terms" element={<Legal slugOverride="terms" />} />
      <Route path="/privacy" element={<Legal slugOverride="privacy" />} />
      <Route path="/shipping-policy" element={<Legal slugOverride="shipping-policy" />} />
    </Routes>
  );
}

function AnalyticsTracker() {
  const location = useLocation();
  const lastFired = useRef("");
  useEffect(() => {
    if (location.pathname === "/admin" || location.pathname.startsWith("/admin/")
        || location.pathname === "/ambassador" || location.pathname.startsWith("/ambassador/")) return;
    const key = `${location.pathname}:${Math.floor(Date.now() / 3000)}`;
    if (lastFired.current === key) return;
    lastFired.current = key;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/collect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: location.pathname, referrer: document.referrer }),
      keepalive: true,
    }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, anchors: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <EmailPopup />
      <PromoBanner />
      <AppRouter />
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 0,
            color: "#fff",
            fontFamily: "'IBM Plex Mono', monospace",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontSize: "12px",
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
